import { useCallback } from 'react';

interface PrintGridOptions {
    rows: any[];
    columns: any[];
    title?: string;
    idField?: string;
    parentIdField?: string;
    indentColumn?: string;
}

export const useGridPrint = () => {
    const printGrid = useCallback(({
        rows,
        columns,
        title,
        idField = 'id',
        parentIdField = 'parentId',
        indentColumn
    }: PrintGridOptions) => {
        // Calculate depth helper
        const getDepth = (row: any, allRows: any[]) => {
            let depth = 0;
            let current = row;
            while (current && current[parentIdField]) {
                depth++;
                current = allRows.find(r => r[idField] === current[parentIdField]);
            }
            return depth;
        };

        const tableHeader = columns
            .map(col => `<th style="text-align:left; padding: 8px; border-bottom: 2px solid #ddd; font-family: Arial, sans-serif;">${col.headerName || ''}</th>`)
            .join('');

        const tableBody = rows.map(row => {
            const depth = indentColumn ? getDepth(row, rows) : 0;

            const cells = columns
                .map(col => {
                    let val = row[col.field];
                    if (val === null || val === undefined) val = "";

                    let style = "padding: 8px; border-bottom: 1px solid #eee; font-family: Arial, sans-serif;";
                    if (col.field === indentColumn && depth > 0) {
                        style += ` padding-left: ${depth * 20 + 8}px;`;
                    }

                    return `<td style="${style}">${
                        // Optional: Add indicator if indented
                        col.field === indentColumn && depth > 0 ? '' : ''
                        }${val}</td>`;
                })
                .join('');
            return `<tr>${cells}</tr>`;
        }).join('');

        const html = `
            <html>
                <head>
                    <title>${title || 'Yazdır'}</title>
                    <style>
                        @media print {
                            @page { margin: 20px; }
                            body { -webkit-print-color-adjust: exact; }
                        }
                        body { font-family: Poppins, sans-serif; font-size: 12px; color: #333; margin: 20px; }
                        h2 { text-align: center; margin-bottom: 20px; font-family: Poppins, sans-serif; }
                        table { width: 100%; border-collapse: collapse; }
                        th { font-weight: bold; background-color: #f8f8f8; color: #333; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                    </style>
                </head>
                <body>
                    <h2>${title || ''}</h2>
                    <table>
                        <thead><tr>${tableHeader}</tr></thead>
                        <tbody>${tableBody}</tbody>
                    </table>
                </body>
            </html>
        `;

        // Create a hidden iframe for seamless printing
        const iframeId = 'grid-print-iframe';
        let iframe = document.getElementById(iframeId) as HTMLIFrameElement;

        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = iframeId;
            iframe.style.position = 'fixed';
            iframe.style.top = '0';
            iframe.style.left = '0';
            iframe.style.width = '0px';
            iframe.style.height = '0px';
            iframe.style.border = 'none';
            iframe.style.visibility = 'hidden';
            iframe.style.pointerEvents = 'none'; // Ensure it doesn't interfere with clicks
            document.body.appendChild(iframe);
        }

        const doc = iframe.contentWindow?.document;
        if (doc) {
            doc.open();
            doc.write(html);
            doc.close();

            // Wait for content to render, then print
            setTimeout(() => {
                if (iframe.contentWindow) {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                }
            }, 500);
        }
    }, []);

    return { printGrid };
};
